import { supabase } from '../../lib/supabaseClient';
import { normalizeCollectionHeaders } from '../../lib/collectionHeaders';

export const ALL_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];
export const PRODUCT_STATUSES = ['draft', 'published', 'hidden', 'out_of_stock', 'archived'];

export async function checkIsAdmin() {
  if (!supabase) return false;

  const { data, error } = await supabase.rpc('is_admin');
  if (!error) {
    return Boolean(data);
  }

  const { data: sessionData } = await supabase.auth.getUser();
  const userId = sessionData?.user?.id;
  if (!userId) return false;

  const { data: row } = await supabase
    .from('admin_users')
    .select('user_id')
    .eq('user_id', userId)
    .maybeSingle();

  return Boolean(row);
}

export async function uploadProductImage(file, folder = 'products') {
  const originalExt = (file.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '');
  const ext = originalExt || 'jpg';
  // Avoid spaces/parentheses in object keys — those break unquoted CSS url()
  const path = `${folder}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;

  const { error } = await supabase.storage
    .from('product-images')
    .upload(path, file, { cacheControl: '3600', upsert: false });

  if (error) throw error;

  const { data } = supabase.storage.from('product-images').getPublicUrl(path);
  return data.publicUrl;
}

export async function fetchDistinctLicenseBrands() {
  const { data, error } = await supabase
    .from('brands')
    .select('name, slug')
    .order('display_order', { ascending: true });

  if (error) {
    if (/brands/i.test(error.message || '')) return [];
    throw error;
  }

  return (data || []).map(row => row.name).filter(Boolean);
}

export async function fetchBrands() {
  const { data, error } = await supabase
    .from('brands')
    .select('*')
    .order('display_order', { ascending: true });

  if (error) throw error;
  return data || [];
}

export async function saveBrand(brand) {
  const payload = {
    ...brand,
    name: brand.name?.trim(),
    slug: brand.slug?.trim(),
    updated_at: new Date().toISOString(),
  };

  if (brand.id) {
    const { data, error } = await supabase
      .from('brands')
      .update(payload)
      .eq('id', brand.id)
      .select('*')
      .single();
    if (error) throw error;
    return data;
  }

  const { id, ...insertPayload } = payload;
  const { data, error } = await supabase
    .from('brands')
    .insert(insertPayload)
    .select('*')
    .single();
  if (error) throw error;
  return data;
}

export async function deleteBrand(brandId) {
  const { error } = await supabase.from('brands').delete().eq('id', brandId);
  if (error) throw error;
}

export async function reorderBrands(brands) {
  await Promise.all(
    brands.map((brand, index) =>
      supabase
        .from('brands')
        .update({ display_order: index + 1 })
        .eq('id', brand.id)
    )
  );
}

export async function fetchAdminProducts() {
  const { data, error } = await supabase
    .from('products')
    .select(`
      id,
      name,
      brand,
      category,
      price,
      sale_price,
      sku,
      status,
      hide_product,
      image_url,
      created_at,
      product_images (id, image_url, sort_order, is_thumbnail),
      product_sizes (id, size_label, stock_quantity, enabled)
    `)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

export async function fetchAdminProduct(productId) {
  const { data, error } = await supabase
    .from('products')
    .select(`
      *,
      product_images (*),
      product_sizes (*),
      product_variants (*)
    `)
    .eq('id', productId)
    .single();

  if (error) throw error;

  const images = [...(data.product_images || [])].sort((a, b) => a.sort_order - b.sort_order);
  const sizes = [...(data.product_sizes || [])].sort((a, b) => a.sort_order - b.sort_order);
  const variants = [...(data.product_variants || [])].sort((a, b) => {
    if (Boolean(b.is_default) !== Boolean(a.is_default)) {
      return Number(Boolean(b.is_default)) - Number(Boolean(a.is_default));
    }
    return a.sort_order - b.sort_order;
  });

  return { ...data, images, sizes, variants };
}

export function getProductStock(product) {
  const sizes = product.product_sizes || product.sizes || [];
  const total = sizes.reduce((sum, s) => sum + (Number(s.stock_quantity) || 0), 0);
  const reserved = Number(product.reserved_stock) || 0;
  return {
    total,
    reserved,
    available: Math.max(total - reserved, 0),
  };
}

const OPTIONAL_PRODUCT_COLUMNS = ['sport', 'league', 'team', 'brand_slug', 'license_brand', 'apparel_type', 'gender'];

const COLUMN_MIGRATION_HINTS = {
  sport: 'supabase-sports.sql',
  league: 'supabase-sports.sql',
  team: 'supabase-sports.sql',
  brand_slug: 'supabase-brands.sql',
  license_brand: 'supabase-brands.sql',
  apparel_type: 'supabase-filters.sql',
  gender: 'supabase-filters.sql',
};

function missingColumnFromError(error, columns = OPTIONAL_PRODUCT_COLUMNS) {
  const message = error?.message || '';
  if (!/(column|schema cache|does not exist)/i.test(message)) return null;
  return columns.find(column => new RegExp(`\\b${column}\\b`, 'i').test(message)) || null;
}

function omitProductColumn(body, column) {
  const { [column]: _removed, ...rest } = body;
  if (column === 'sport' && body.sport) {
    rest.category = body.sport;
  }
  if (column === 'team' && body.team) {
    rest.brand = body.team;
  }
  return rest;
}

function migrationErrorForColumns(columns) {
  const migrations = [...new Set(columns.map(column => COLUMN_MIGRATION_HINTS[column]))]
    .filter(Boolean)
    .join(' and ');

  return new Error(
    `Could not save ${columns.join(', ')}. Run ${migrations} in the Supabase SQL Editor, then try again.`
  );
}

function hasFieldValue(payload, column) {
  return payload[column] != null && payload[column] !== '';
}

async function persistProduct(payload, { mode, productId } = {}) {
  let body = { ...payload };
  let result;

  for (let attempt = 0; attempt <= OPTIONAL_PRODUCT_COLUMNS.length; attempt += 1) {
    result =
      mode === 'insert'
        ? await supabase.from('products').insert(body).select('*').single()
        : await supabase.from('products').update(body).eq('id', productId).select('*').single();

    if (!result.error) return result.data;

    const missing = missingColumnFromError(result.error);
    if (!missing || !Object.prototype.hasOwnProperty.call(body, missing)) {
      throw result.error;
    }

    // Never silently drop league / apparel category / gender — those have no legacy column.
    if (
      (missing === 'league' ||
        missing === 'brand_slug' ||
        missing === 'license_brand' ||
        missing === 'apparel_type' ||
        missing === 'gender') &&
      hasFieldValue(payload, missing)
    ) {
      throw migrationErrorForColumns([missing]);
    }

    body = omitProductColumn(body, missing);
  }

  throw result.error;
}

export async function createProduct(payload) {
  return persistProduct(
    {
      ...payload,
      status: payload.status || 'published',
      allow_search: payload.allow_search !== false,
      show_in_category: payload.show_in_category !== false,
      hide_product: Boolean(payload.hide_product),
    },
    { mode: 'insert' }
  );
}

export async function updateProduct(productId, payload) {
  return persistProduct(
    {
      ...payload,
      status: payload.status || 'published',
      updated_at: new Date().toISOString(),
    },
    { mode: 'update', productId }
  );
}

export async function deleteProduct(productId) {
  const { error } = await supabase.from('products').delete().eq('id', productId);
  if (error) throw error;
}

export async function replaceProductImages(productId, images) {
  await supabase.from('product_images').delete().eq('product_id', productId);

  if (!images.length) return [];

  const rows = images.map((img, index) => ({
    product_id: productId,
    image_url: img.image_url,
    sort_order: index,
    is_thumbnail: Boolean(img.is_thumbnail),
    variant_id: img.variant_id || null,
    alt_text: img.alt_text || null,
  }));

  const { data, error } = await supabase
    .from('product_images')
    .insert(rows)
    .select('*');

  if (error) throw error;
  return data || [];
}

export async function replaceProductSizes(productId, sizes) {
  await supabase.from('product_sizes').delete().eq('product_id', productId);

  const rows = ALL_SIZES.map((label, index) => {
    const match = sizes.find(s => s.size_label === label) || {};
    return {
      product_id: productId,
      size_label: label,
      stock_quantity: Number(match.stock_quantity) || 0,
      sku: match.sku || null,
      enabled: match.enabled !== false,
      sort_order: index + 1,
    };
  });

  const { data, error } = await supabase
    .from('product_sizes')
    .insert(rows)
    .select('*');

  if (error) throw error;
  return data || [];
}

export async function replaceProductVariants(productId, variants) {
  const { data: existing } = await supabase
    .from('product_variants')
    .select('id')
    .eq('product_id', productId);

  const keepIds = variants.filter(v => v.id && !String(v.id).startsWith('temp-')).map(v => v.id);
  const toDelete = (existing || []).filter(v => !keepIds.includes(v.id)).map(v => v.id);

  if (toDelete.length) {
    await supabase.from('product_variants').delete().in('id', toDelete);
  }

  const hasDefault = variants.some(variant => variant.is_default);
  const saved = [];

  for (let i = 0; i < variants.length; i += 1) {
    const variant = variants[i];
    const payload = {
      product_id: productId,
      name: variant.name,
      color_hex: variant.color_hex || null,
      sort_order: i,
      is_default: hasDefault ? Boolean(variant.is_default) : i === 0,
    };

    if (variant.id && !String(variant.id).startsWith('temp-')) {
      let { data, error } = await supabase
        .from('product_variants')
        .update(payload)
        .eq('id', variant.id)
        .select('*')
        .single();

      if (error && /is_default|column/i.test(error.message || '')) {
        const { is_default, ...legacy } = payload;
        ({ data, error } = await supabase
          .from('product_variants')
          .update(legacy)
          .eq('id', variant.id)
          .select('*')
          .single());
      }

      if (error) throw error;
      saved.push(data);
    } else {
      let { data, error } = await supabase
        .from('product_variants')
        .insert(payload)
        .select('*')
        .single();

      if (error && /is_default|column/i.test(error.message || '')) {
        const { is_default, ...legacy } = payload;
        ({ data, error } = await supabase
          .from('product_variants')
          .insert(legacy)
          .select('*')
          .single());
      }

      if (error) throw error;
      saved.push(data);
    }
  }

  return saved;
}

export async function logStockChange({ productId, sizeLabel, changeAmount, reason, userId }) {
  const { error } = await supabase.from('stock_history').insert({
    product_id: productId,
    size_label: sizeLabel || null,
    change_amount: changeAmount,
    reason: reason || null,
    created_by: userId || null,
  });
  if (error) throw error;
}

export async function fetchStockHistory(productId) {
  const { data, error } = await supabase
    .from('stock_history')
    .select('*')
    .eq('product_id', productId)
    .order('created_at', { ascending: false })
    .limit(50);

  if (error) throw error;
  return data || [];
}

export async function fetchAdminOrders() {
  const { data, error } = await supabase
    .from('orders')
    .select(`
      id,
      order_number,
      user_id,
      status,
      payment_status,
      fulfilment_status,
      total,
      shipping_name,
      shipping_email,
      created_at,
      order_items (
        id,
        product_name,
        price,
        quantity,
        image_url,
        size_label
      )
    `)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

export async function fetchAdminOrder(orderId) {
  const { data, error } = await supabase
    .from('orders')
    .select(`
      *,
      order_items (*)
    `)
    .eq('id', orderId)
    .single();

  if (error) throw error;
  return data;
}

export async function updateOrderStatus(orderId, updates) {
  const { data, error } = await supabase
    .from('orders')
    .update(updates)
    .eq('id', orderId)
    .select('*')
    .single();

  if (error) throw error;
  return data;
}

export async function fetchCategories() {
  const { data, error } = await supabase
    .from('categories')
    .select('*')
    .order('display_order', { ascending: true });

  if (error) throw error;
  return data || [];
}

export async function saveCategory(category) {
  if (category.id) {
    const { data, error } = await supabase
      .from('categories')
      .update({ ...category, updated_at: new Date().toISOString() })
      .eq('id', category.id)
      .select('*')
      .single();
    if (error) throw error;
    return data;
  }

  const { id, ...payload } = category;
  const { data, error } = await supabase
    .from('categories')
    .insert(payload)
    .select('*')
    .single();
  if (error) throw error;
  return data;
}

export async function deleteCategory(categoryId) {
  const { error } = await supabase.from('categories').delete().eq('id', categoryId);
  if (error) throw error;
}

export async function reorderCategories(categories) {
  await Promise.all(
    categories.map((cat, index) =>
      supabase
        .from('categories')
        .update({ display_order: index + 1 })
        .eq('id', cat.id)
    )
  );
}

export async function fetchDiscounts() {
  const { data, error } = await supabase
    .from('discounts')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

export async function saveDiscount(discount) {
  if (discount.id) {
    const { data, error } = await supabase
      .from('discounts')
      .update(discount)
      .eq('id', discount.id)
      .select('*')
      .single();
    if (error) throw error;
    return data;
  }

  const { id, ...payload } = discount;
  const { data, error } = await supabase
    .from('discounts')
    .insert(payload)
    .select('*')
    .single();
  if (error) throw error;
  return data;
}

export async function deleteDiscount(discountId) {
  const { error } = await supabase.from('discounts').delete().eq('id', discountId);
  if (error) throw error;
}

export async function fetchCustomers() {
  const { data, error } = await supabase
    .from('orders')
    .select('user_id, shipping_name, shipping_email, created_at, total')
    .order('created_at', { ascending: false });

  if (error) throw error;

  const map = new Map();
  (data || []).forEach(order => {
    const key = order.user_id || order.shipping_email;
    if (!map.has(key)) {
      map.set(key, {
        id: key,
        name: order.shipping_name,
        email: order.shipping_email,
        orders_count: 0,
        total_spent: 0,
        last_order_at: order.created_at,
      });
    }
    const customer = map.get(key);
    customer.orders_count += 1;
    customer.total_spent += Number(order.total) || 0;
    if (new Date(order.created_at) > new Date(customer.last_order_at)) {
      customer.last_order_at = order.created_at;
      customer.name = order.shipping_name;
      customer.email = order.shipping_email;
    }
  });

  return Array.from(map.values());
}

export async function fetchDashboardStats() {
  const { data, error } = await supabase.rpc('admin_dashboard_stats');
  if (error) throw error;
  return data;
}

export async function fetchStoreSettings() {
  const { data, error } = await supabase
    .from('store_settings')
    .select('*')
    .eq('id', 1)
    .maybeSingle();

  if (error) throw error;
  return data;
}

export async function saveStoreSettings(settings) {
  const { data, error } = await supabase
    .from('store_settings')
    .upsert({ ...settings, id: 1, updated_at: new Date().toISOString() })
    .select('*')
    .single();

  if (error) throw error;
  return data;
}

export async function fetchHomepageSettings() {
  const { data, error } = await supabase
    .from('homepage_settings')
    .select('*')
    .eq('id', 1)
    .maybeSingle();

  if (error) throw error;
  return data;
}

/**
 * Persist collection headers (Shop + sport / league / team / brand), including crop data.
 * Prefers a security-definer RPC so the write cannot silently no-op under RLS.
 */
export async function saveCollectionHeaders(headers) {
  const collection_headers = normalizeCollectionHeaders(headers);

  const rpc = await supabase.rpc('admin_set_collection_headers', {
    headers: collection_headers,
  });

  if (!rpc.error) {
    return normalizeCollectionHeaders(rpc.data);
  }

  // RPC missing → fall back to direct update
  if (!/admin_set_collection_headers|Could not find|schema cache/i.test(rpc.error.message || '')) {
    throw rpc.error;
  }

  const { data, error } = await supabase
    .from('homepage_settings')
    .update({
      collection_headers,
      updated_at: new Date().toISOString(),
    })
    .eq('id', 1)
    .select('collection_headers')
    .maybeSingle();

  if (error) throw error;

  if (!data) {
    throw new Error(
      'Collection headers did not save. Sign in again as an admin and retry.'
    );
  }

  return normalizeCollectionHeaders(data.collection_headers);
}

export async function saveHomepageSettings(settings) {
  const collection_headers = normalizeCollectionHeaders(settings.collection_headers);
  const payload = {
    hero_image_url: settings.hero_image_url ?? null,
    hero_images: settings.hero_images || [],
    hero_title: settings.hero_title ?? null,
    hero_subtitle: settings.hero_subtitle ?? null,
    collection_headers,
    id: 1,
    updated_at: new Date().toISOString(),
  };

  let { data, error } = await supabase
    .from('homepage_settings')
    .upsert(payload)
    .select('*')
    .maybeSingle();

  if (error && /hero_images/i.test(error.message || '')) {
    const { hero_images, ...legacy } = payload;
    ({ data, error } = await supabase
      .from('homepage_settings')
      .upsert(legacy)
      .select('*')
      .maybeSingle());
  }

  if (error) throw error;
  if (!data) {
    throw new Error(
      'Homepage settings did not save. Sign in again as an admin and retry.'
    );
  }
  return data;
}

export function slugify(text) {
  return String(text || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}
